import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Flame, Snowflake, AlertCircle, ChevronDown, Image, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEngine } from '../hooks/useEngine.js';
import { buildHeatMap } from '../lib/formulas.js';
import { history as baseHistory } from '../data/history.js';
import { useHistoryStore } from '../stores/history.js';
import { exportTrendToImage, downloadImage, shareImage } from '../lib/trendExport.js';
import clsx from 'clsx';

// 统计 N 期内每个数字出现次数
function tallyDigits(hist, n) {
  const cnt = {};
  hist.slice(0, n).forEach(h => [h.a, h.b, h.c].forEach(d => cnt[d] = (cnt[d] || 0) + 1));
  const total = n * 3;
  const out = {};
  for (let i = 0; i < 10; i++) {
    out[i] = cnt[i] || 0;
    out[`${i}_pct`] = total > 0 ? (out[i] / total * 100) : 0;
  }
  return out;
}

// 统计跨度/和值/形态
function buildDist(hist, n) {
  const span = {}, sum = {}, type = { '组六': 0, '组三': 0, '豹子': 0 };
  hist.slice(0, n).forEach(h => {
    span[h.span] = (span[h.span] || 0) + 1;
    sum[h.sum] = (sum[h.sum] || 0) + 1;
    type[h.type] = (type[h.type] || 0) + 1;
  });
  return { span, sum, type };
}

// 遗漏检测:对每个号/跨度/和值,找"连续遗漏 N 期" 或 "连续出现 N 期"
// 返回: {
//   value → {
//     type: 'miss'|'hit',          // 当前是连续遗漏中还是连续命中中
//     count: number,               // 连续期数
//     lastIdx: number,             // 上次出现的位置(0 = 最新期)
//     avgMiss: number,              // 历史平均遗漏(基于更长窗口,默认 200 期)
//     deviation: number,            // count - avgMiss,正数=冷 负数=热
//     signal: 'rebound'|'kill'|'normal',  // 信号:反弹候选/杀号候选/正常
//     hitRate: number,              // 在历史窗口内出现频率(0-1)
//   }
// }
function findOmissions(hist, keyFn, range = 30, avgWindow = 200) {
  // 1) 算最近 30 期的连续 streak
  const recent = hist.slice(0, range);
  const streaks = {};
  recent.forEach((h, i) => {
    const val = keyFn(h);
    const vals = Array.isArray(val) ? val : [val];
    vals.forEach(v => {
      // v 是 number 0-9,JS 内部 Object key 实际是 string,但访问 streaks[v] 都 OK
      const s = streaks[v];
      if (!s) {
        streaks[v] = { type: 'hit', count: 1, lastIdx: i };
      } else if (s.type === 'hit') {
        // 上一期也出现 → 连续命中 +1
        streaks[v] = { type: 'hit', count: s.count + 1, lastIdx: i };
      } else {
        // 上一期没出现,这一期出现 → 切回 hit,count=1
        streaks[v] = { type: 'hit', count: 1, lastIdx: i };
      }
    });
    // 已记录的号,这期没出现 → 切到 miss(count 续 +1)
    // 用 Object.keys 拿到 string 索引,再 parseInt 转 number 比较
    Object.keys(streaks).forEach(k => {
      const v = parseInt(k, 10);
      if (vals.includes(v)) return;  // 这期已出现,跳过
      const s = streaks[v];
      if (s.type === 'hit') {
        // 上一期出现,这一期没出现 → 切到 miss,count=1
        streaks[v] = { type: 'miss', count: 1, lastIdx: i };
      } else {
        // 已经在 miss → 连续遗漏 +1
        streaks[v] = { type: 'miss', count: s.count + 1, lastIdx: i };
      }
    });
  });

  // 2) 算历史平均遗漏(基于更长窗口) + 频率
  const longWindow = hist.slice(0, avgWindow);
  const appearCount = {};
  longWindow.forEach(h => {
    const val = keyFn(h);
    const vals = Array.isArray(val) ? val : [val];
    vals.forEach(v => {
      appearCount[v] = (appearCount[v] || 0) + 1;
    });
  });

  // 3) 给每个 streak 补上 avgMiss / deviation / signal / hitRate
  const totalLong = longWindow.length || 1;
  Object.keys(streaks).forEach(k => {
    const v = parseInt(k, 10);
    const cnt = appearCount[v] || 0;
    const avg = cnt > 0 ? +(totalLong / cnt).toFixed(1) : totalLong;
    const s = streaks[v];
    s.avgMiss = avg;
    s.hitRate = +(cnt / totalLong).toFixed(3);
    s.deviation = +(s.count - avg).toFixed(1);

    if (s.type === 'miss') {
      if (avg > 0 && s.count > avg * 1.5) {
        s.signal = 'rebound';
      } else {
        s.signal = 'normal';
      }
    } else {
      // hit 状态:连续命中 ≥ 5 → 过热杀号候选
      s.signal = s.count >= 5 ? 'kill' : 'normal';
    }
  });

  return streaks;
}

export default function TrendPage() {
  const { heatMap } = useEngine();
  const history = useHistoryStore(s => s.history);
  const [period, setPeriod] = useState(30);

  const recent30 = useMemo(() => tallyDigits(history, 30), [history[0]?.p]);
  const recent50 = useMemo(() => tallyDigits(history, 50), [history[0]?.p]);
  const recent100 = useMemo(() => tallyDigits(history, 100), [history[0]?.p]);

  const dist = useMemo(() => buildDist(history, period), [period, history[0]?.p]);

  // 遗漏检测(包 try/catch 防御,任何错误都不影响其他页面渲染)
  const omissions30 = useMemo(() => {
    try {
      const digitMiss = findOmissions(history, h => [h.a, h.b, h.c], 30);
      return digitMiss;
    } catch (e) {
      console.error('[OmissionCard] findOmissions error:', e);
      return {};
    }
  }, [history[0]?.p]);

  const handleExport = () => {
    try {
      const url = exportTrendToImage(history, { periodCount: 30 });
      if (!url) { toast.error('导出失败'); return; }
      downloadImage(url, `3d-max-trend-${new Date().toISOString().slice(0,10)}.png`);
      toast.success('已下载走势图');
    } catch (e) { toast.error('导出失败:' + e.message); }
  };

  const handleShare = async () => {
    try {
      const url = exportTrendToImage(history, { periodCount: 30 });
      if (!url) { toast.error('生成失败'); return; }
      const res = await shareImage(url, '3D MAX 走势图');
      toast.success(res === 'shared' ? '已分享' : res === 'copied' ? '已复制到剪贴板' : '已下载');
    } catch (e) { toast.error('分享失败'); }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 size={22} className="text-brand-cyan" />
              数据可视化
            </h1>
            <p className="text-sm text-ink-3 mt-1">走势 + 热号 + 冷号 + 多时间窗口交叉分析 + 遗漏检测</p>
          </div>
          <div className="flex gap-1.5">
            <button onClick={handleExport} className="btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1">
              <Image size={12} />
              导出图
            </button>
            <button onClick={handleShare} className="btn-secondary text-xs px-2.5 py-1.5 flex items-center gap-1">
              <Share2 size={12} />
              分享
            </button>
          </div>
        </div>
      </div>

      {/* 遗漏检测(最有价值) */}
      <OmissionCard omissions={omissions30} />

      {/* 最近 30 期开奖 - 固定 5 列 */}
      <div className="card">
        <div className="text-sm font-semibold mb-3">最近 30 期开奖</div>
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-1.5">
          {history.slice(0, 30).map((h, i) => (
            <div
              key={h.p}
              className={clsx(
                'px-1.5 py-1.5 rounded-md text-xs font-mono text-center',
                'bg-white/5 border border-white/10',
                i === 0 && 'ring-2 ring-brand-gold/50'
              )}
              title={`${h.p} ${h.d} 和=${h.sum} 跨=${h.span} ${h.type}`}
            >
              <div className="text-[9px] text-ink-3">{h.p.slice(-3)}</div>
              <div className="text-brand-gold font-bold text-sm">
                {h.a}{h.b}{h.c}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Heat map multi window — 显示出现次数 + 百分比 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: '近 30 期', data: recent30, color: 'brand-cyan', n: 30 },
          { label: '近 50 期', data: recent50, color: 'brand-purple', n: 50 },
          { label: '近 100 期', data: recent100, color: 'brand-gold', n: 100 },
        ].map((w, i) => (
          <motion.div
            key={w.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="card"
          >
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Flame size={14} className={`text-${w.color}`} />
              {w.label}热号
              <span className="text-[10px] text-ink-3 font-normal ml-auto">理论 {10}%</span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {Array.from({ length: 10 }, (_, n) => n).map(n => {
                const count = w.data[n] || 0;
                const pct = w.data[`${n}_pct`] || 0;
                const max = Math.max(...Array.from({length:10}, (_, i) => w.data[i] || 0));
                const intensity = max > 0 ? count / max : 0;
                return (
                  <div
                    key={n}
                    className="rounded-lg flex flex-col items-center justify-center p-1.5"
                    style={{
                      background: `rgba(0, 245, 212, ${intensity * 0.4 + 0.05})`,
                      border: `1px solid rgba(0, 245, 212, ${intensity * 0.6 + 0.2})`,
                    }}
                    title={`号 ${n}: ${count} 次, ${pct.toFixed(1)}%`}
                  >
                    <div className="font-mono font-bold text-base">{n}</div>
                    <div className="text-[9px] text-ink-3 leading-tight">{count}次</div>
                    <div className="text-[9px] text-brand-cyan font-mono leading-tight">{pct.toFixed(1)}%</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      {/* 跨度/和值/形态 - 切换 + 移动端友好 */}
      <div className="card !p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
            <TrendingUp size={14} className="text-brand-rose" />
            跨度 / 和值 / 形态
          </div>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DistributionCard
          title="跨度分布"
          icon={<TrendingUp size={14} className="text-brand-rose" />}
          data={dist.span}
          max={9}
          total={period}
        />
        <DistributionCard
          title="和值分布"
          icon={<TrendingUp size={14} className="text-brand-cyan" />}
          data={dist.sum}
          max={27}
          total={period}
        />
        <div className="card">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Snowflake size={14} className="text-brand-purple" />
            形态分布
          </div>
          <div className="space-y-2">
            {Object.entries(dist.type).map(([k, v]) => {
              const pct = (v / period * 100).toFixed(0);
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1">
                    <span>{k}</span>
                    <span className="font-mono">{v} / {period} = <span className="text-brand-cyan font-bold">{pct}%</span></span>
                  </div>
                  <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        k === '组六' && 'bg-brand-cyan',
                        k === '组三' && 'bg-brand-rose',
                        k === '豹子' && 'bg-brand-gold',
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 012路 / 奇偶 / 大小 - 移动端 2 列 */}
      <div className="card">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span>奇偶 / 大小 / 012路</span>
          <span className="text-[10px] text-ink-3 font-normal">近 {period} 期</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(() => {
            const stats = { odd: 0, even: 0, big: 0, small: 0, route0: 0, route1: 0, route2: 0 };
            history.slice(0, period).forEach(h => {
              [h.a, h.b, h.c].forEach(d => {
                if (d % 2 === 1) stats.odd++; else stats.even++;
                if (d >= 5) stats.big++; else stats.small++;
                stats[['route0','route1','route2'][d % 3]]++;
              });
            });
            const total = period * 3;
            return [
              { label: '奇数', value: stats.odd, color: 'bg-brand-rose' },
              { label: '偶数', value: stats.even, color: 'bg-brand-cyan' },
              { label: '大(5-9)', value: stats.big, color: 'bg-brand-gold' },
              { label: '小(0-4)', value: stats.small, color: 'bg-brand-purple' },
            ].map(s => {
              const pct = (s.value / total * 100).toFixed(0);
              return (
                <div key={s.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-ink-2">{s.label}</span>
                    <span className="font-mono">{s.value} / {total} = <span className={s.color.replace('bg-', 'text-')}>{pct}%</span></span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}

// 遗漏检测卡(最有价值)
function OmissionCard({ omissions }) {
  const entries = Object.entries(omissions)
    .map(([v, info]) => ({ code: parseInt(v), ...info }))
    .filter(x => typeof x.count === 'number' && x.count >= 4);

  // 排序:miss 多的排前(冷)
  entries.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'miss' ? -1 : 1;
    return b.count - a.count;
  });

  const missList = entries.filter(x => x.type === 'miss').slice(0, 8);
  const hitList = entries.filter(x => x.type === 'hit').slice(0, 5);

  // 单个号码卡(带多维数据)
  const renderChip = (m) => {
    const isRebound = m.signal === 'rebound';
    const isKill = m.signal === 'kill';
    const isHotMiss = m.type === 'miss';
    return (
      <div
        key={m.code}
        className={clsx(
          'px-2 py-1.5 rounded text-center min-w-[58px] border',
          isHotMiss
            ? (isRebound ? 'bg-brand-rose/15 border-brand-rose/50' : 'bg-brand-purple/10 border-brand-purple/30')
            : (isKill ? 'bg-brand-gold/15 border-brand-gold/50' : 'bg-brand-gold/10 border-brand-gold/30'),
        )}
        title={`号 ${m.code}: ${isHotMiss ? `连续 ${m.count} 期未出现` : `连续 ${m.count} 期都出现`} | 历史平均遗漏 ${m.avgMiss} 期 | 偏差 ${m.deviation > 0 ? '+' : ''}${m.deviation} 期`}
      >
        <div className={clsx(
          'font-mono font-bold text-sm',
          isHotMiss ? 'text-brand-purple' : 'text-brand-gold'
        )}>
          {m.code}
          {(isRebound || isKill) && (
            <span className={clsx(
              'ml-1 text-[9px] font-normal px-1 rounded',
              isRebound ? 'bg-brand-rose text-white' : 'bg-ink-3 text-bg-base'
            )}>
              {isRebound ? '反弹' : '过热'}
            </span>
          )}
        </div>
        <div className="text-[9px] text-ink-3 leading-tight mt-0.5">
          {isHotMiss ? '漏' : '中'}{m.count} / 均{m.avgMiss}
        </div>
        <div className={clsx(
          'text-[9px] leading-tight font-mono',
          m.deviation > 0 ? 'text-brand-rose' : 'text-ink-3'
        )}>
          偏差 {m.deviation > 0 ? '+' : ''}{m.deviation}
        </div>
      </div>
    );
  };

  const reboundCount = missList.filter(x => x.signal === 'rebound').length;
  const killCount = hitList.filter(x => x.signal === 'kill').length;

  return (
    <div className="card border-brand-rose/30">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <AlertCircle size={14} className="text-brand-rose" />
        <div className="text-sm font-semibold">遗漏检测(近 30 期)</div>
        <div className="text-[10px] text-ink-3 ml-auto">
          连续遗漏 ≥ 4 / 历史平均 200 期
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-xs text-ink-3 text-center py-3">
          暂无显著遗漏/连胜模式
        </div>
      ) : (
        <div className="space-y-2">
          {missList.length > 0 && (
            <div>
              <div className="text-[10px] text-brand-rose font-semibold mb-1.5 flex items-center gap-1.5">
                <span>❄ 连续遗漏(冷倾向)</span>
                {reboundCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-brand-rose/20 text-brand-rose font-bold">
                    🎯 {reboundCount} 个反弹候选
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {missList.map(renderChip)}
              </div>
            </div>
          )}
          {hitList.length > 0 && (
            <div>
              <div className="text-[10px] text-brand-gold font-semibold mb-1.5 flex items-center gap-1.5">
                <span>🔥 连续命中(热倾向)</span>
                {killCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-ink-3/20 text-ink-2 font-bold">
                    ⚔ {killCount} 个杀号候选
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {hitList.map(renderChip)}
              </div>
            </div>
          )}
          <div className="text-[10px] text-ink-3 leading-relaxed pt-1 border-t border-white/5">
            💡 <b>反弹候选</b>:当前连续遗漏 &gt; 历史平均 × 1.5(回归均值的统计倾向,非因果反弹) ·
            <b>杀号候选</b>:连续命中 ≥ 5 期(过热可能冷却)
          </div>
        </div>
      )}
    </div>
  );
}

// 移动端友好的时间窗口选择
function PeriodSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [30, 50, 100, 200, 500];
  return (
    <div className="relative">
      {/* 桌面:横排按钮 */}
      <div className="hidden sm:flex items-center gap-1 bg-bg-base rounded-lg p-0.5">
        {options.map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={clsx(
              'px-2.5 py-0.5 rounded text-[11px] transition whitespace-nowrap',
              value === n
                ? 'bg-brand-cyan/20 text-brand-cyan'
                : 'text-ink-3 hover:text-ink-1'
            )}
          >
            近 {n} 期
          </button>
        ))}
      </div>
      {/* 移动:下拉 */}
      <div className="sm:hidden">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-2.5 py-1 bg-bg-base rounded-lg text-xs"
        >
          近 {value} 期
          <ChevronDown size={12} className={clsx('transition', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-10 bg-bg-card border border-white/10 rounded-lg shadow-xl overflow-hidden">
            {options.map(n => (
              <button
                key={n}
                onClick={() => { onChange(n); setOpen(false); }}
                className={clsx(
                  'block w-full px-4 py-1.5 text-xs text-left whitespace-nowrap',
                  value === n ? 'bg-brand-cyan/20 text-brand-cyan' : 'hover:bg-white/5'
                )}
              >
                近 {n} 期
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DistributionCard({ title, icon, data, max, total }) {
  const values = Array.from({ length: max + 1 }, (_, i) => data[i] || 0);
  const peak = Math.max(...values);
  const expected = total / (max + 1);
  return (
    <div className="card">
      <div className="text-sm font-semibold mb-3 flex items-center gap-2">
        {icon}
        {title}
        <span className="text-[10px] text-ink-3 font-normal ml-auto">理论 {expected.toFixed(1)}/值</span>
      </div>
      {/* 横向滚动:移动端 28 个柱子不挤 */}
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex items-end gap-0.5 h-28 min-w-fit">
          {values.map((v, i) => {
            const pct = total > 0 ? (v / total * 100).toFixed(0) : 0;
            const ratio = expected > 0 ? (v / expected) : 0;
            const isPeak = v === peak && v > 0;
            // 移动端:只显示偶数标签(0, 2, 4...)省空间
            const showLabel = i % 2 === 0;
            return (
              <div key={i} className="flex-1 min-w-[10px] flex flex-col items-center gap-0.5" title={`值 ${i}: ${v} 次, ${pct}%`}>
                <div className="text-[8px] sm:text-[9px] font-mono text-brand-cyan font-bold leading-tight h-3">
                  {v > 0 && v}
                </div>
                <div
                  className={clsx(
                    'w-full rounded-t transition-all',
                    isPeak ? 'bg-gradient-to-t from-brand-gold to-brand-rose' : 'bg-gradient-to-t from-brand-purple to-brand-cyan',
                  )}
                  style={{ height: `${peak > 0 ? v / peak * 100 : 0}%`, minHeight: v > 0 ? '4px' : '0' }}
                />
                <div className="text-[8px] sm:text-[9px] text-ink-3 font-mono leading-tight h-3">
                  {showLabel && i}
                </div>
                <div className={clsx(
                  'text-[8px] font-mono leading-tight h-3',
                  ratio > 1.2 ? 'text-brand-gold font-bold' : ratio < 0.8 ? 'text-brand-purple' : 'text-ink-3'
                )}>
                  {v > 0 && `${pct}%`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
